import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import {
  isTaggedMessage,
  tagMessage,
  type InboundMessage,
  type OutboundMessage,
  type PlayerOptions,
} from "src/ad-preview-frame/messages";

type EventMap = {
  ready: () => void;
  frameChanged: (frame: number) => void;
  playStateChanged: (isPlaying: boolean) => void;
  muteStateChanged: (isMuted: boolean) => void;
  sizeChanged: (size: { width: number; height: number }) => void;
  error: (error: { kind: string; message: string; stack?: string }) => void;
  keydown: (key: string) => void;
};

/**
 * Wraps an `<iframe>` hosting the preview player. Sends typed inbound
 * messages, dispatches outbound messages to subscribers, and caches the
 * latest frame/playback state so consumers can read it synchronously.
 *
 * Messages sent before the iframe emits `ready` are queued and flushed
 * once it does — callers don't need to wait.
 */
export class PreviewIframeClient {
  private readonly iframe: HTMLIFrameElement;
  private readonly targetOrigin: string | null;
  private isReady = false;
  private queue: InboundMessage[] = [];
  private listeners: { [K in keyof EventMap]: Set<EventMap[K]> } = {
    ready: new Set(),
    frameChanged: new Set(),
    playStateChanged: new Set(),
    muteStateChanged: new Set(),
    sizeChanged: new Set(),
    error: new Set(),
    keydown: new Set(),
  };

  private cachedFrame = 0;
  private cachedIsPlaying = false;
  private cachedIsMuted = false;

  /**
   * @param targetOrigin origin the frame is served from, or `null` when it is
   *   framed at an opaque origin. An opaque origin can only be addressed as
   *   `"*"`; a real one is named explicitly so the composition and its
   *   presigned asset URLs are not delivered to a frame that navigated away.
   */
  constructor(iframe: HTMLIFrameElement, targetOrigin: string | null) {
    this.iframe = iframe;
    this.targetOrigin = targetOrigin;
    window.addEventListener("message", this.handleMessage);
  }

  destroy(): void {
    window.removeEventListener("message", this.handleMessage);
    for (const set of Object.values(this.listeners)) {
      set.clear();
    }
    this.queue = [];
  }

  // ─── Inbound (parent → iframe) ─────────────────────────────────────────

  setComposition(
    composition: VideoComposition,
    componentAssets: Record<string, string>,
    resolvedRefs: Record<string, string>,
  ): void {
    this.send({
      type: "setComposition",
      composition,
      componentAssets,
      resolvedRefs,
    });
  }

  setFontRules(fontRules: string[]): void {
    this.send({ type: "setFontRules", fontRules });
  }

  setPlayerOptions(options: PlayerOptions): void {
    this.send({ type: "setPlayerOptions", options });
  }

  play(): void {
    this.send({ type: "play" });
  }

  pause(): void {
    this.send({ type: "pause" });
  }

  toggle(): void {
    this.send({ type: "toggle" });
  }

  seekTo(frame: number): void {
    this.send({ type: "seekTo", frame });
  }

  mute(): void {
    this.send({ type: "mute" });
  }

  unmute(): void {
    this.send({ type: "unmute" });
  }

  // ─── Cached state (mirrors PlayerRef sync API) ─────────────────────────

  getCurrentFrame(): number {
    return this.cachedFrame;
  }

  isPlaying(): boolean {
    return this.cachedIsPlaying;
  }

  isMuted(): boolean {
    return this.cachedIsMuted;
  }

  // ─── Event subscription ────────────────────────────────────────────────

  on<K extends keyof EventMap>(type: K, listener: EventMap[K]): () => void {
    this.listeners[type].add(listener as never);
    return () => {
      this.listeners[type].delete(listener as never);
    };
  }

  // ─── Internals ─────────────────────────────────────────────────────────

  private send(message: InboundMessage): void {
    if (!this.isReady) {
      this.queue.push(message);
      return;
    }
    this.postNow(message);
  }

  private postNow(message: InboundMessage): void {
    const target = this.iframe.contentWindow;
    if (!target) {
      console.warn("[preview-iframe] no contentWindow when sending", message);
      return;
    }
    target.postMessage(tagMessage(message), this.targetOrigin ?? "*");
  }

  private handleMessage = (event: MessageEvent): void => {
    // Only accept messages from our iframe. At an opaque origin `event.origin`
    // is "null" and carries no information, so the source check is the only
    // gate available; when the frame has a real origin we check that too.
    if (event.source !== this.iframe.contentWindow) return;
    if (this.targetOrigin !== null && event.origin !== this.targetOrigin)
      return;
    if (!isTaggedMessage<OutboundMessage>(event.data)) return;

    const message = event.data.payload;
    switch (message.type) {
      case "ready":
        this.isReady = true;
        for (const queued of this.queue) this.postNow(queued);
        this.queue = [];
        this.emit("ready");
        break;
      case "frameChanged":
        this.cachedFrame = message.frame;
        this.emit("frameChanged", message.frame);
        break;
      case "playStateChanged":
        this.cachedIsPlaying = message.isPlaying;
        this.emit("playStateChanged", message.isPlaying);
        break;
      case "muteStateChanged":
        this.cachedIsMuted = message.isMuted;
        this.emit("muteStateChanged", message.isMuted);
        break;
      case "sizeChanged":
        this.emit("sizeChanged", {
          width: message.width,
          height: message.height,
        });
        break;
      case "error":
        this.emit("error", {
          kind: message.kind,
          message: message.message,
          stack: message.stack,
        });
        break;
      case "keydown":
        this.emit("keydown", message.key);
        break;
    }
  };

  private emit<K extends keyof EventMap>(
    type: K,
    ...args: Parameters<EventMap[K]>
  ): void {
    for (const listener of this.listeners[type]) {
      (listener as (...args: unknown[]) => void)(...args);
    }
  }
}
